<template>
  <div class="video-lib">
    <header class="toolbar">
      <button class="icon-btn back-btn" @click="$router.push({ name: 'VideoIndex' })" aria-label="返回主題">
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="title-center">
        <h2 class="toolbar-title">{{ topicName || '主題' }}</h2>
        <p class="sub">
          {{ topicSubName || (hasSubtopics ? '選擇小主題進入' : '此主題的影片列表') }}
        </p>
      </div>
    </header>

    <section class="list">
      <div v-if="loading" class="hint">載入中…</div>

      <!-- A) 有小主題 -->
      <template v-else-if="hasSubtopics">
        <div v-if="subtopics.length === 0" class="hint">此主題目前沒有小主題</div>
        <div class="grid">
          <article v-for="s in subtopics" :key="s.id" class="card" @click="gotoSubtopic(s.id)">
            <img :src="s.cover_url || placeholder16x9" alt="" class="thumb16x9" loading="lazy" />
            <div class="meta">
              <div class="vtitle">{{ s.name }}</div>
              <div class="vsubtitle" v-if="s.sub_name">{{ s.sub_name }}</div>
              <div class="count">共 {{ (s.videos && s.videos[0] ? s.videos[0].count : 0) }} 部影片</div>
            </div>
          </article>
        </div>
      </template>


      <!-- B) 無小主題：直接列影片 -->
      <template v-else>
        <div v-if="videos.length === 0" class="hint">這個主題目前沒有影片</div>
        <div class="grid">
          <article v-for="v in videos" :key="v.id" class="card" @click="gotoPlay(v.id)">
            <img :src="v.thumb_url || placeholder16x9" alt="" class="thumb16x9" loading="lazy" />
            <div class="meta">
              <div class="vtitle">{{ v.title }}</div>
              <!-- <div class="tags">
                <span v-for="k in (v.keywords || [])" :key="k" class="tag">#{{ k }}</span>
              </div> -->
            </div>
          </article>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '@/lib/supabase'
import i18n from '@/i18n'  // ← 新增

// 目前語言（兼容 string/ref）
const currentLocale = computed(() => {
  const l = i18n.global.locale
  return typeof l === 'string' ? l : l.value || 'zh-TW'
})

const route = useRoute()
const router = useRouter()
const topicId = route.params.topicId

const topicName = ref('')
const topicSubName = ref('')
const hasSubtopics = ref(false)
const subtopics = ref([])
const videos = ref([])
const loading = ref(false)
const placeholder16x9 = 'https://placehold.co/480x270?text=TSK'

// 🔁 REPLACE
async function loadTopicName() {
  const L = currentLocale.value

  // 抓當前語言 + zh-TW 作為 fallback
  const { data, error } = await supabase
    .from('video_topic_i18n')
    .select('topic_id, locale, name, sub_name')
    .eq('topic_id', topicId)
    .in('locale', [L, 'zh-TW'])

  if (error) {
    console.error('[loadTopicName]', error)
    topicName.value = ''
    topicSubName.value = ''
    return
  }

  const zh = (data || []).find(r => r.locale === 'zh-TW')
  const cur = (data || []).find(r => r.locale === L)
  topicName.value = (cur?.name || zh?.name || '')
  topicSubName.value = (cur?.sub_name || zh?.sub_name || '')
}

async function detectHasSubtopics() {
  const { count, error } = await supabase
    .from('video_subtopics')
    .select('id', { count: 'exact', head: true })
    .eq('topic_id', topicId)
  hasSubtopics.value = !error && (count ?? 0) > 0
}

// 🔁 REPLACE
async function loadSubtopics() {
  const L = currentLocale.value

  // A) 先抓 base（拿 id、計數、排序）
  const { data: base, error: e1 } = await supabase
    .from('video_subtopics')
    .select('id, cover_url, slug, sort, created_at, videos(count)')
    .eq('topic_id', topicId)
    .order('sort', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(200)

  if (e1) {
    console.error('[loadSubtopics base]', e1)
    subtopics.value = []
    return
  }

  if (!base || base.length === 0) { subtopics.value = []; return }

  const ids = base.map(s => s.id)

  // B) 再抓 i18n：當前語言 + zh-TW
  const { data: i18nRows, error: e2 } = await supabase
    .from('video_subtopic_i18n')
    .select('subtopic_id, locale, name, sub_name, cover_url')
    .in('subtopic_id', ids)
    .in('locale', [L, 'zh-TW'])

  if (e2) console.error('[loadSubtopics i18n]', e2)

  // 合併（zh-TW 先入，再用 L 覆蓋）
  const nameMap = new Map()
  const subNameMap = new Map()
  const coverMap = new Map()

  ;(i18nRows || []).filter(r => r.locale === 'zh-TW').forEach(r => {
    nameMap.set(r.subtopic_id, r.name || '')
    subNameMap.set(r.subtopic_id, r.sub_name || '')
    coverMap.set(r.subtopic_id, r.cover_url || '')
  })
  ;(i18nRows || []).filter(r => r.locale === L).forEach(r => {
    if (r.name) nameMap.set(r.subtopic_id, r.name)
    if (r.sub_name) subNameMap.set(r.subtopic_id, r.sub_name)
    if (r.cover_url) coverMap.set(r.subtopic_id, r.cover_url)
  })

  subtopics.value = base.map(s => ({
    id: s.id,
    name: nameMap.get(s.id) || '(未翻譯)',
    sub_name: subNameMap.get(s.id) || '',
    cover_url: coverMap.get(s.id) || s.cover_url || '',
    slug: s.slug,
    sort: s.sort,
    created_at: s.created_at,
    videos: s.videos, // [{ count }]
  }))
}

// 🔁 REPLACE
async function loadTopicVideos() {
  const L = currentLocale.value

  // A) 先抓 base videos（負責排序、created_at 等）
  const { data: base, error: e1 } = await supabase
    .from('videos')
    .select('id, thumb_url, storage_path, created_at, sort')
    .eq('topic_id', topicId)
    .order('sort', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(100)

  if (e1) {
    console.error('[loadTopicVideos base]', e1)
    videos.value = []
    return
  }
  if (!base || base.length === 0) { videos.value = []; return }

  const ids = base.map(v => v.id)

  // B) 抓 i18n（當前語言 + zh-TW）
  const { data: i18nRows, error: e2 } = await supabase
    .from('video_i18n')
    .select('video_id, locale, title, thumb_url')
    .in('video_id', ids)
    .in('locale', [L, 'zh-TW'])

  if (e2) console.error('[loadTopicVideos i18n]', e2)

  // 合併（zh-TW 先入，再用 L 覆蓋）
  const titleMap = new Map()
  const thumbMap = new Map()

  ;(i18nRows || []).filter(r => r.locale === 'zh-TW').forEach(r => {
    titleMap.set(r.video_id, r.title || '')
    thumbMap.set(r.video_id, r.thumb_url || '')
  })
  ;(i18nRows || []).filter(r => r.locale === L).forEach(r => {
    if (r.title) titleMap.set(r.video_id, r.title)
    if (r.thumb_url) thumbMap.set(r.video_id, r.thumb_url)
  })

  videos.value = base.map(v => ({
    id: v.id,
    title: titleMap.get(v.id) || '(未翻譯)',
    thumb_url: thumbMap.get(v.id) || v.thumb_url || '',
  }))
}

function gotoSubtopic(id){ router.push({ name: 'VideoSubtopic', params: { subId: id } }) }
function gotoPlay(id){ router.push({ name: 'VideoPlay', params: { id } }) }

async function boot() {
  loading.value = true
  await loadTopicName()
  await detectHasSubtopics()
  if (hasSubtopics.value) await loadSubtopics()
  else await loadTopicVideos()
  loading.value = false
}

onMounted(boot)
watch(() => route.params.topicId, boot)
// 語言切換 → 重新載入
watch(currentLocale, async (n, o) => {
  if (n !== o) await boot()
})
</script>

<style scoped>
.video-lib { display:flex; flex-direction:column; gap:16px; }

.toolbar {
  position: relative; /* ✅ 讓 back-btn 以 toolbar 為定位基準 */

  background:#fff;
  border:1px solid #eee;
  border-radius:14px;

  display:flex;
  align-items:center;
  gap:12px;

  padding:14px 16px 14px 56px; /* 左邊留 back 鍵空間 */
  min-height:68px;
}

.back-btn {
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px; height:36px;
  border-radius:10px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111;
  cursor:pointer;
}

.title-center {
  position: static;
  transform: none;
  text-align: center;
  width: 100%;
  pointer-events: auto;

  overflow-wrap: anywhere;
  word-break: break-word;
}

.toolbar-title { margin:0; font-size:20px; font-weight:700; line-height:1.25; white-space: normal; }
.sub { color:#666; margin-top:6px; font-size:14px; line-height:1.35; white-space: normal; }

.list { background:#fff; border:1px solid #eee; border-radius:14px; padding:14px; min-height:480px; }
.hint { color:#666; padding:20px; }
.grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; }
@media (max-width:1200px){ .grid{ grid-template-columns: repeat(2, minmax(0,1fr)); } }
.card { background:#fafafa; border:1px solid #eee; border-radius:12px; overflow:hidden; cursor:pointer; transition: transform .12s ease, box-shadow .12s ease; }
.card:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.06); }
.thumb16x9 { width:100%; aspect-ratio:16/9; object-fit:cover; display:block; background:#ddd; }
.meta { padding:10px 12px; }
.vtitle { font-weight:600; margin-bottom:6px; }
.count, .tag { font-size:12px; color:#666; margin-right:6px; }
</style>
